/**
 * Money parsing + formatting helpers.
 *
 * SharePoint columns typed as "Currency" or "Number" arrive as JS numbers,
 * but legacy PowerApps flows occasionally wrote Spanish-locale strings
 * (`"3107812,5"`). The plain number field can also be `null`. `parseMoney`
 * accepts both shapes and returns `null` for anything unparseable — the
 * render layer then shows an em-dash instead of `NaN`.
 */
export function parseMoney(v: unknown): number | null {
  if (v == null) return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string') {
    const trimmed = v.trim();
    if (!trimmed) return null;
    // Accept "1.234,56" or "1234,56" or "1234.56". Strip thousand-separator
    // dots when a comma is present; then swap the comma for a dot.
    const normalized = trimmed.includes(',')
      ? trimmed.replace(/\./g, '').replace(',', '.')
      : trimmed;
    const n = Number(normalized);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

export type CurrencyCode = 'ARS' | 'MXN' | 'USD';

/**
 * Translate the SP-style currency label that lives on `Moneda_*` fields
 * into the typed `CurrencyCode`. Replaces the ad-hoc inline ternary
 * `f.Moneda_X === 'Dolares' ? 'USD' : 'ARS'` scattered across the app —
 * needed once MXN landed because that ternary silently miscategorises
 * Mexican rows as ARS. Defaults to 'ARS' for back-compat with legacy
 * AR rows that have null/empty Moneda_X.
 */
export function currencyFromSp(s: string | null | undefined): CurrencyCode {
  const v = (s ?? '').trim();
  if (v === 'USD' || v === 'Dolares' || v === 'Dólares') return 'USD';
  if (v === 'MXN' || v === 'Pesos Mexicanos') return 'MXN';
  return 'ARS';
}

/** Inverse of `currencyFromSp` — produces the SP-style label for a PG currency. */
export function currencyToSp(code: CurrencyCode): string {
  if (code === 'USD') return 'Dolares';
  if (code === 'MXN') return 'Pesos Mexicanos';
  return 'Pesos Argentinos';
}

/**
 * Locale picked off the currency so MX users see MXN in `es-MX` conventions
 * (period decimal, comma thousands) while AR users keep their `es-AR` look.
 * USD piggybacks on whichever country is active — we default to es-AR for
 * back-compat, since today's USD usage is AR-side.
 */
function localeFor(currency: CurrencyCode): string {
  return currency === 'MXN' ? 'es-MX' : 'es-AR';
}

export function formatMoney(
  v: unknown,
  currency: CurrencyCode,
  fallback = '—',
): string {
  const n = parseMoney(v);
  if (n == null) return fallback;
  return new Intl.NumberFormat(localeFor(currency), {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(n);
}
